import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, FormikProvider, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { getPaymentDate } from '../../../../redux/features/generalSlice';
import MotionWrapper from '../../../../components/MotionWrapper';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Icon } from '@iconify/react';
import useResponsive from '../../../../Hooks/useResponsive';

const Days = [
  {
    id: 0,
    name: 'Sunday',
  },
  {
    id: 1,
    name: 'Monday',
  },
  {
    id: 2,
    name: 'Tuesday',
  },
  {
    id: 3,
    name: 'Wednesday',
  },
  {
    id: 4,
    name: 'Thursday',
  },
  {
    id: 5,
    name: 'Friday',
  },
  {
    id: 6,
    name: 'Saturday',
  },
];

const PercentageCoursePymentCheck = ({ optionTermId }) => {
  const smUp = useResponsive('up', 'sm');

  const mdUp = useResponsive('up', 'md');
  const { loading, paymentDates } = useSelector((state) => state.general);
  const { classroomRange } = useSelector((state) => state.cart);
  const [min, setMin] = React.useState(null);
  const [response, setResponse] = React.useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const schema = Yup.object({
    preferredDays: Yup.array().of(Yup.number().required('Day is required')).min(1, 'At least one day must be selected'),
    totalSessions: Yup.string().required('Total Sessions is required'),
    totalAmount: Yup.string().required('Total Amount is required'),
    firstPayingAmount: Yup.string().required('First Paying Amount is required'),
  });
  const formik = useFormik({
    initialValues: {
      courseoptionId: optionTermId || '',
      firstPaymentDate: dayjs().format('YYYY-MM-DD'),
      preferredDays: [],
      courseType: 2,
      totalSessions: '',
      totalAmount: '',
      currencyType: 1,
      firstPayingAmount: '',
    },
    validationSchema: schema,
    onSubmit: (values) => {
      dispatch(getPaymentDate(values)).then((res) => {
        if (res.payload.status === 'failed') {
          enqueueSnackbar(res.payload.message, {
            variant: 'error',
          });
        } else if (res.payload.status === 'success') {
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });
          setResponse(res.payload.data);
        }
      });
    },
  });

  const handleChange = (event) => {
    if (event.target.value) {
      formik.setFieldValue('currencyType', event.target.value);
    }
  };

  const data =
    formik.values.currencyType === 1
      ? classroomRange.dataInr
      : formik.values.currencyType === 2
      ? classroomRange.dataAed
      : [];

  function getThresholdAmount(totalAmount) {
    let threshold = null;
    for (const item of data) {
      if (item.upperLimit === 'default') {
        threshold = item.threshold; // fallback default
      } else if (totalAmount <= parseInt(item.upperLimit)) {
        threshold = item.threshold;
        break;
      }
    }

    const thresholdAmount = (totalAmount * threshold) / 100;

    return {
      totalAmount,
      thresholdPercentage: threshold,
      thresholdAmount,
    };
  }
  useEffect(() => {
    if (formik.values.totalAmount) {
      //   formik.setFieldValue(
      //     "firstPayingAmount",
      //     getThresholdAmount(formik.values.totalAmount).thresholdAmount
      //   );
      setMin(getThresholdAmount(formik.values.totalAmount).thresholdAmount);
    }
  }, [formik.values.totalAmount]);
  return (
    <Box sx={{ padding: 2, bgcolor: 'grey.200', borderRadius: 2 }}>
      <Stack direction={smUp ? 'row' : 'column'} spacing={2}>
        <Box sx={{ width: '100%' }}>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <MotionWrapper directions={'right'} delay={0.5}>
                <Stack direction={'column'} spacing={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="First Payment Date"
                      value={dayjs(formik.values.firstPaymentDate)}
                      onChange={(newValue) =>
                        formik.setFieldValue('firstPaymentDate', dayjs(newValue).format('YYYY-MM-DD'))
                      }
                    />
                  </LocalizationProvider>
                  <Autocomplete
                    multiple
                    options={Days}
                    getOptionLabel={(option) => option.name}
                    value={Days.filter((s) => formik.values.preferredDays?.includes(s.id)) || []}
                    onChange={(event, newValue) => {
                      const daysIds = newValue.map((day) => day.id);
                      formik.setFieldValue('preferredDays', daysIds);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Days"
                        variant="outlined"
                        error={formik.touched.preferredDays && Boolean(formik.errors.preferredDays)}
                        helperText={formik.touched.preferredDays && formik.errors.preferredDays}
                      />
                    )}
                  />
                  <TextField
                    name="totalSessions"
                    label="Total Sessions"
                    onWheel={(e) => e.target.blur()}
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '[0-9]*[.]?[0-9]+',
                      onWheel: (e) => e.target.blur(),
                    }}
                    value={formik.values.totalSessions}
                    onChange={formik.handleChange}
                    type="number"
                    fullWidth
                    error={formik.touched.totalSessions && Boolean(formik.errors.totalSessions)}
                    helperText={formik.touched.totalSessions && formik.errors.totalSessions}
                  />
                  {/* ------------------------------------------ */}
                  <Box sx={{ minWidth: 120 }}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">Currency Type</InputLabel>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={formik.values.currencyType}
                        label="Currency Type"
                        onChange={handleChange}
                      >
                        <MenuItem value={1}>INR</MenuItem>
                        <MenuItem value={2}>AED</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {/* ------------------------------------------------------ */}
                  <TextField
                    name="totalAmount"
                    label="Total Amount"
                    onWheel={(e) => e.target.blur()}
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '[0-9]*[.]?[0-9]+',
                      onWheel: (e) => e.target.blur(),
                    }}
                    value={formik.values.totalAmount}
                    onChange={formik.handleChange}
                    type="number"
                    fullWidth
                    error={formik.touched.totalAmount && Boolean(formik.errors.totalAmount)}
                    helperText={formik.touched.totalAmount && formik.errors.totalAmount}
                  />

                  <TextField
                    name="firstPayingAmount"
                    label="First Paying Amount"
                    onWheel={(e) => e.target.blur()}
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '[0-9]*[.]?[0-9]+',
                      onWheel: (e) => e.target.blur(),
                    }}
                    value={formik.values.firstPayingAmount}
                    onChange={formik.handleChange}
                    type="number"
                    fullWidth
                    error={formik.touched.firstPayingAmount && Boolean(formik.errors.firstPayingAmount)}
                    helperText={formik.touched.firstPayingAmount && formik.errors.firstPayingAmount}
                  />
                  {min > formik.values.firstPayingAmount ||
                  formik.values.totalAmount < formik.values.firstPayingAmount ? (
                    <Alert severity="error">
                      {min > formik.values.firstPayingAmount
                        ? ` Mimimum paying amount must be greater than or eqaul to
                      ${min}`
                        : formik.values.totalAmount < formik.values.firstPayingAmount
                        ? 'Paying amount must be less than or equal to total amount'
                        : null}
                    </Alert>
                  ) : null}

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      disabled={
                        loading ||
                        min == null ||
                        min > formik.values.firstPayingAmount ||
                        formik.values.totalAmount < formik.values.firstPayingAmount
                      }
                      sx={{
                        color: 'white',
                        background: `linear-gradient(to bottom, #0089d0 0%, #0089d0 10%, #a125c2 100%)`,
                        borderRadius: 4,
                      }}
                      size="small"
                      type="submit"
                    >
                      <Icon icon="solar:calendar-broken" width="24" height="24" />
                      {loading ? 'Please wait...' : 'Check Date'}
                    </Button>
                  </Box>
                </Stack>
              </MotionWrapper>
            </Form>
          </FormikProvider>
        </Box>
        {response ? (
          <Box sx={{ width: smUp ? '50%' : '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Stack direction={'column'} spacing={1}>
                <Chip
                  variant="contained"
                  color="primary"
                  size="small"
                  icon={<Icon icon="solar:calendar-broken" width="20" height="20" />}
                  label={`Course Start Date :${response?.courseStartDate}`}
                />
                <Chip
                  size="small"
                  variant="contained"
                  color="primary"
                  icon={<Icon icon="solar:calendar-broken" width="20" height="20" />}
                  label={`Course End Date :${response?.courseEndDate}`}
                />
              </Stack>
            </Box>
            <MotionWrapper directions={'left'} delay={0.5}>
              <TableContainer sx={{ mt: 4.2 }} component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>amount</TableCell>
                      <TableCell align="right">payable Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {response?.sessionDates?.map((row, index) => (
                      <>
                        {/* {index === 0 || index === 1 ? ( */}
                        <>
                          <TableRow
                            key={row.payableDate}
                            sx={{
                              '&:last-child td, &:last-child th': {
                                border: 0,
                              },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {row.amount}
                            </TableCell>
                            <TableCell align="right">{row.payableDate}</TableCell>
                          </TableRow>
                        </>
                        {/* ) : null} */}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MotionWrapper>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export default PercentageCoursePymentCheck;
